import React from 'react'
import { useTranslation } from 'react-i18next'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import {
  type BrandGroup,
  useInvalidateSurveyForSettingsQuery,
} from '~/hooks/useSurveyForSettingsQuery'
import { exists, getByPath } from '~/utils'

import { useSurveyId } from './useSurveyId'

const surveyDocument = graphql(`
  query BrandGroupListPage_Survey($surveyId: ID!, $brandGroupId: Int!) {
    survey(id: $surveyId) {
      id
      answerFileUploadedAt
      brandGroup(id: $brandGroupId) {
        id
        name
        segment_definitions {
          segment_number
          condition_groups {
            question_id
          }
        }
      }
      questionnaireFileUploadedAt
    }
  }
`)

const mappingDocument = graphql(`
  mutation BrandGroupListPage_Mapping($brandGroupId: Int!) {
    mapping(brandGroupId: $brandGroupId) {
      awaitId
    }
  }
`)

export const useMapping = () => {
  const graphQLRequest = useGraphQLRequest()
  const surveyId = useSurveyId()
  const invalidateSurveyForSettingsQuery = useInvalidateSurveyForSettingsQuery()
  const { t } = useTranslation()

  type Result =
    | {
        success: false
        error: string
      }
    | {
        success: true
        awaitId: string
      }

  return React.useCallback(
    async (brandGroup: BrandGroup): Promise<Result> => {
      const brandGroupId = brandGroup.id

      const surveyResult = await graphQLRequest({
        document: surveyDocument,
        variables: { surveyId, brandGroupId },
      })
      const survey = surveyResult.survey
      if (!exists(survey?.answerFileUploadedAt)) {
        return {
          success: false,
          error: t('could_not_proceed_because_response_data_was_not_uploaded'),
        }
      }

      if (!exists(survey?.questionnaireFileUploadedAt)) {
        return {
          success: false,
          error: t(
            'could_not_proceed_because_questionnaire_data_was_not_uploaded',
          ),
        }
      }

      const validSegmentDefinitions = (
        survey?.brandGroup?.segment_definitions ?? []
      ).filter((segmentdefinition) => {
        const conditionGroup =
          getByPath(segmentdefinition, 'condition_groups.0') ?? []

        return 0 < conditionGroup.length
      })
      if (validSegmentDefinitions.length !== 9) {
        return {
          success: false,
          error: t(
            'could_not_proceed_because_not_all_segment_definition_were_registered',
          ),
        }
      }

      const mappingResult = await graphQLRequest({
        document: mappingDocument,
        variables: { brandGroupId },
      })

      const awaitId = mappingResult.mapping?.awaitId
      if (!exists(awaitId)) {
        return { success: false, error: t('mapping_process_failed') }
      }

      invalidateSurveyForSettingsQuery()

      return { success: true, awaitId }
    },
    [graphQLRequest, invalidateSurveyForSettingsQuery, surveyId, t],
  )
}
